import React from 'react';

/**
 * Los nueve criterios de la guía, con la frase de la llamada que sustenta cada
 * veredicto (ticket #97).
 *
 * POR QUÉ ES UN COMPONENTE COMPARTIDO
 *   Porque lo ven el supervisor (`CallDetail`) y el agente (`AgentCallDetail`),
 *   y tiene que ser palabra por palabra lo mismo. El agente NO lo veía: su
 *   pantalla mostraba el checklist y la boleta v2, pero nunca los nueve
 *   criterios ni sus citas. Ése era el hueco que hacía imposible pedirle una
 *   réplica — se le pedía firmar sobre una calificación cuya justificación no
 *   tenía enfrente.
 *
 * LA CITA CUANDO EL CRITERIO FALLA
 *   Hasta la migración 022, el extracto sólo se llenaba en los criterios
 *   CUMPLIDOS: el prompt pedía explícitamente `null` cuando el veredicto era
 *   "no". O sea que había evidencia exactamente donde nadie discute, y ninguna
 *   donde sí se discute. Ahora la cita de un "no" es el momento donde la
 *   conducta se esperaba y no apareció — normalmente la pregunta del cliente que
 *   quedó sin responder.
 *
 *   Las boletas anteriores a ese cambio siguen sin cita en sus "no", y eso se
 *   dice con palabras en vez de dejar un renglón en blanco: un hueco sin
 *   explicación se lee como una falla del sistema.
 */

const ETIQUETAS = {
  saludo_institucional: 'Saludo institucional',
  sondeo_informacion: 'Sondeo de la necesidad',
  perfilamiento_huesped: 'Perfilamiento del huésped',
  recomendaciones_personalizadas: 'Recomendaciones personalizadas',
  cierre_venta_propuesta: 'Cierre de venta propuesto',
  evaluacion_upsell: 'Evaluación de upsell',
  manejo_objeciones: 'Manejo de objeciones',
  confirmacion_reservacion: 'Confirmación de la reservación',
  despedida_institucional: 'Despedida institucional',
};

const VERDE = '#34d399';
const ROJO = '#f87171';
const GRIS = '#6b7280';

const color = (v) => (v === 'si' ? VERDE : v === 'no' ? ROJO : GRIS);
const texto = (v) => (v === 'si' ? 'Sí cumplió' : v === 'no' ? 'No cumplió' : 'No aplicó');

export default function CriteriosDeLaGuia({ validators, titulo = 'Los nueve criterios de la guía' }) {
  const entradas = Object.entries(validators || {});
  if (!entradas.length) return null;

  const th = {
    textAlign: 'left', padding: '6px 8px', fontSize: 11, color: GRIS,
    textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #26262e',
    fontWeight: 600,
  };
  const td = { padding: '8px', fontSize: 13, borderBottom: '1px solid #1c1c22', verticalAlign: 'top' };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-title">{titulo}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: '30%' }}>Criterio</th>
              <th style={{ ...th, width: 110 }}>Veredicto</th>
              <th style={th}>Lo que dice la llamada</th>
            </tr>
          </thead>
          <tbody>
            {entradas.map(([clave, v]) => {
              const cumple = v?.cumple;
              return (
                <tr key={clave} style={{ opacity: cumple === 'na' || cumple == null ? 0.55 : 1 }}>
                  <td style={{ ...td, fontWeight: 600 }}>
                    {ETIQUETAS[clave] || clave.replace(/_/g, ' ')}
                  </td>
                  <td style={{ ...td, color: color(cumple), fontWeight: 600 }}>
                    {texto(cumple)}
                  </td>
                  <td style={td}>
                    {v?.extracto ? (
                      <span style={{ fontStyle: 'italic', color: '#c4b5fd' }}>«{v.extracto}»</span>
                    ) : cumple === 'na' ? (
                      <span style={{ color: GRIS }}>
                        No se presentó la situación que este criterio evalúa, así que sale
                        del cálculo.
                      </span>
                    ) : (
                      <span style={{ color: GRIS }}>
                        Sin cita registrada. Las boletas calificadas antes de agosto de 2026
                        sólo guardaban la frase cuando el criterio se cumplía.
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, fontSize: 10.5, color: '#4b5563', lineHeight: 1.6 }}>
        La cita es una frase literal de la transcripción. Cuando el criterio no se
        cumplió, señala el momento donde la conducta se esperaba y no apareció — no es
        un comentario del sistema.
      </div>
    </div>
  );
}
